<template>
	<div
		v-if="modelValue"
		ref="panel"
		class="qc-comments-panel elevation-6 d-flex flex-column"
		:style="panelStyle"
		@keydown.stop
		@keyup.stop
		@keypress.stop
	>
		<!-- Draggable header -->
		<div
			class="d-flex align-center px-3 py-2 border-b flex-shrink-0 panel-header"
		>
			<v-icon
				size="14"
				class="mr-1 drag-handle"
				color="grey-darken-2"
				@mousedown="startDrag"
			>
				mdi-drag
			</v-icon>
			<v-icon size="18" class="mr-2">mdi-comment-text-outline</v-icon>
			<span class="text-body-2 font-weight-medium">QC Comments</span>
			<v-chip
				v-if="unresolvedCount > 0"
				size="x-small"
				variant="flat"
				class="ml-2 bg-valence-attention-tint text-valence-attention-ink"
			>
				{{ unresolvedCount }} open
			</v-chip>
			<v-spacer />
			<v-btn
				icon="mdi-close"
				size="x-small"
				variant="text"
				@click="$emit('update:modelValue', false)"
			/>
		</div>

		<div
			v-if="extractionLocked"
			class="d-flex align-start ga-2 px-3 py-2 border-b flex-shrink-0 text-caption text-grey-darken-2"
			data-testid="qc-comments-locked-note"
		>
			<v-icon size="14" class="flex-shrink-0">mdi-lock-outline</v-icon>
			<span>
				Read only: this paper is in quality control. To make a change, ask the
				QC reviewer to flag a comment for rework.
			</span>
		</div>

		<!-- Comments list -->
		<div ref="commentList" class="flex-1 overflow-y-auto pa-2">
			<div v-if="loading" class="d-flex justify-center pa-4">
				<v-progress-circular indeterminate size="24" color="secondary" />
			</div>

			<div v-else-if="comments.length === 0" class="text-center pa-4">
				<v-icon size="32" color="grey-lighten-1" class="mb-2">
					mdi-comment-check-outline
				</v-icon>
				<div class="text-body-2 text-grey-darken-2">
					No QC comments for this extraction
				</div>
			</div>

			<div
				v-for="comment in comments"
				:key="comment.uuid"
				:data-comment-uuid="comment.uuid"
				class="comment-item pa-2 mb-2 rounded"
				style="position: relative"
				:class="{
					'rework-item': comment.rework,
					'active-dp-item': isCommentForActiveDataPoint(comment),
					'highlighted-comment': isHighlightedComment(comment),
				}"
			>
				<span v-if="comment.read === false" class="unread-dot" />
				<v-btn
					v-else-if="comment.read !== false && !isOwnReply(comment)"
					icon="mdi-email-mark-as-unread"
					size="x-small"
					variant="text"
					title="Mark unread"
					color="grey-darken-1"
					class="mark-unread-btn comment-unread-btn"
					@click="markUnread(comment)"
				/>
				<!-- Author and time -->
				<div class="d-flex align-center mb-1">
					<span class="text-caption font-weight-medium">
						{{ getUserName(comment.user) }}
					</span>
					<!-- DCTL-2294: rework is the WORKFLOW axis, not a valence. It
					     used to be color="error" here and amber in CommentPopover -
					     one datum, two hues. Rust + the ruled glyph, off the shared
					     map, so a re-bucketing needs no edit here. -->
					<v-chip
						v-if="comment.rework"
						size="x-small"
						variant="flat"
						:class="`bg-${reworkChip.tint} text-${reworkChip.ink} ml-1`"
						:prepend-icon="reworkChip.icon"
					>
						{{ reworkChip.label }}
					</v-chip>
					<v-chip
						v-if="comment.resolved"
						size="x-small"
						variant="flat"
						class="ml-1 bg-valence-positive-tint text-valence-positive-ink"
					>
						Resolved
					</v-chip>
					<v-spacer />
					<span class="text-caption text-grey-darken-2">
						{{ formatTime(comment.createdAt) }}
					</span>
				</div>

				<!-- Context label (clickable for navigation) -->
				<template v-if="figureViews[comment.uuid]">
					<div
						class="text-caption text-grey-darken-2 mb-1 cursor-pointer navigate-link"
						@click="navigateFigure(figureViews[comment.uuid])"
					>
						<v-icon size="12" class="mr-1">mdi-chart-scatter-plot</v-icon>
						{{ figureViews[comment.uuid].title }}
					</div>
					<ul
						v-if="figureViews[comment.uuid].points.length"
						class="figure-point-list text-caption mb-1"
					>
						<li
							v-for="point in figureViews[comment.uuid].points"
							:key="point.uuid"
							class="cursor-pointer navigate-link text-grey-darken-2"
							@click="$emit('navigate', point.uuid, [point.uuid])"
						>
							{{ point.label }}
						</li>
					</ul>
				</template>
				<div
					v-else-if="comment.context?.label"
					class="text-caption text-grey-darken-2 mb-1 cursor-pointer navigate-link"
					@click="navigateTo(comment)"
				>
					<v-icon size="12" class="mr-1">mdi-link-variant</v-icon>
					{{ comment.context.label }}
				</div>

				<!-- Comment text -->
				<div
					class="text-body-2"
					style="white-space: pre-wrap; word-break: break-word"
				>
					{{ comment.text }}
				</div>

				<!-- Replies -->
				<div
					v-for="reply in comment.replies || []"
					:key="reply.uuid"
					:data-comment-uuid="reply.uuid"
					class="pl-2 mt-1 border-s"
				>
					<div class="d-flex align-center">
						<span class="text-caption font-weight-medium">
							{{ getUserName(reply.user) }}
						</span>
						<v-spacer />
						<span class="text-caption text-grey-darken-1">
							{{ formatTime(reply.createdAt) }}
						</span>
						<v-btn
							v-if="isOwnReply(reply)"
							icon="mdi-delete-outline"
							size="x-small"
							variant="text"
							title="Delete"
							class="delete-btn ml-1"
							@click="deleteReply(reply, comment)"
						/>
						<span v-if="!isOwnReply(reply)" class="reply-action-slot">
							<v-btn
								v-if="reply.read !== false"
								icon="mdi-email-mark-as-unread"
								size="x-small"
								variant="text"
								title="Mark unread"
								color="grey-darken-1"
								class="mark-unread-btn"
								@click="markUnread(reply)"
							/>
							<span v-else class="unread-dot-inline" />
						</span>
					</div>
					<div class="text-body-2">{{ reply.text }}</div>
				</div>

				<!-- Reply input -->
				<div class="d-flex align-center ga-1 mt-2">
					<v-text-field
						v-model="replyTexts[comment.uuid]"
						autocomplete="off"
						variant="outlined"
						density="compact"
						hide-details
						placeholder="Reply..."
						class="text-body-2"
						@keydown.enter.prevent="submitReply(comment)"
					/>
					<v-btn
						icon="mdi-send"
						size="x-small"
						variant="text"
						color="grey-darken-2"
						:disabled="!replyTexts[comment.uuid]?.trim()"
						@click="submitReply(comment)"
					/>
				</div>
			</div>
		</div>

		<!-- Delete confirm dialog -->
		<v-dialog v-model="deleteDialog" max-width="360">
			<v-card>
				<v-card-title class="text-body-1">Delete reply</v-card-title>
				<v-card-text> Are you sure you want to delete this reply? </v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn variant="text" @click="deleteDialog = false"> Cancel </v-btn>
					<v-btn color="error" variant="flat" @click="confirmDelete">
						Delete
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import axios from "axios";
import { useMainStore } from "@/store";
import { WORKFLOW_STATES } from "@/constants/workflowStates";
import { personName } from "@/utils/person";
import {
	commentForUuid,
	figureCommentScope,
	ownDataPointUuids,
} from "@/components/qcCommentTarget";

export default {
	name: "QcCommentsPanel",

	props: {
		sdeUuid: { type: String, required: true },
		modelValue: { type: Boolean, default: false },
		activeDataPointUuid: { type: String, default: null },
		highlightCommentUuid: { type: String, default: null },
		// DCTL-2746. Whether the open extraction has a data point, so a comment
		// links to this curator's own point rather than the other input's.
		ownsDataPoint: { type: Function, default: null },
		// DCTL-2746. (comment, ownUuids) -> { title, graphUuid, compositeKey,
		// points: [{ uuid, label }] } or null: how a figure comment reads in the
		// open extraction. Without it a figure comment shows the QC's label.
		describeFigureComment: { type: Function, default: null },
		// Open the deep-linked comment's target once the comments load.
		landOnHighlighted: { type: Boolean, default: false },
	},

	// navigate(uuid, candidates): the first own point and, for an editor that
	// cannot tell which points it has, every point to try in order.
	// navigate-figure({ graphUuid, compositeKey }): a figure or one series.
	emits: ["update:modelValue", "navigate", "navigate-figure"],

	data() {
		return {
			loading: false,
			comments: [],
			// DCTL-2733: completed, not reopened, and its paper is in QC, so no
			// comment here lets the curator edit until one is flagged for rework.
			extractionLocked: false,
			// The deep-linked comment already opened, so a reload does not move
			// the curator again.
			landedCommentUuid: null,
			replyTexts: {},
			deleteDialog: false,
			deleteTarget: null,
			deleteParent: null,
			// Drag state
			posX: null,
			posY: null,
			dragging: false,
			dragStartX: 0,
			dragStartY: 0,
			dragStartPosX: 0,
			dragStartPosY: 0,
		};
	},

	computed: {
		reworkChip() {
			return WORKFLOW_STATES.rework;
		},
		currentUserId() {
			return useMainStore().user?.id || null;
		},
		unresolvedCount() {
			return this.comments.filter((c) => !c.resolved).length;
		},
		// Each figure comment as the open extraction names it, by comment uuid.
		// Reads the editor's data through `describeFigureComment`, so it follows
		// edits there.
		figureViews() {
			const views = {};
			if (!this.describeFigureComment) return views;
			for (const comment of this.comments) {
				if (!figureCommentScope(comment)) continue;
				const view = this.describeFigureComment(
					comment,
					this.ownPoints(comment),
				);
				if (view) views[comment.uuid] = view;
			}
			return views;
		},
		panelStyle() {
			if (this.posX !== null && this.posY !== null) {
				return {
					left: `${this.posX}px`,
					top: `${this.posY}px`,
					right: "auto",
					bottom: "auto",
				};
			}
			// Default position: bottom-right
			return {
				right: "16px",
				bottom: "80px",
				left: "auto",
				top: "auto",
			};
		},
	},

	watch: {
		modelValue(val) {
			if (val) {
				this.loadComments();
			}
		},
	},

	mounted() {
		if (this.modelValue) {
			this.loadComments();
		}
	},

	beforeUnmount() {
		this.teardownReadObserver();
		document.removeEventListener("mousemove", this.onDrag);
		document.removeEventListener("mouseup", this.stopDrag);
	},

	methods: {
		startDrag(e) {
			// Don't drag if clicking a button
			if (e.target.closest("button")) return;

			this.dragging = true;
			this.dragStartX = e.clientX;
			this.dragStartY = e.clientY;

			const panel = this.$refs.panel;
			const rect = panel.getBoundingClientRect();
			this.dragStartPosX = rect.left;
			this.dragStartPosY = rect.top;

			document.addEventListener("mousemove", this.onDrag);
			document.addEventListener("mouseup", this.stopDrag);
			e.preventDefault();
		},

		onDrag(e) {
			if (!this.dragging) return;

			const dx = e.clientX - this.dragStartX;
			const dy = e.clientY - this.dragStartY;

			// Clamp to viewport
			const panel = this.$refs.panel;
			const w = panel.offsetWidth;
			const h = panel.offsetHeight;
			this.posX = Math.max(
				0,
				Math.min(window.innerWidth - w, this.dragStartPosX + dx),
			);
			this.posY = Math.max(
				0,
				Math.min(window.innerHeight - h, this.dragStartPosY + dy),
			);
		},

		stopDrag() {
			this.dragging = false;
			document.removeEventListener("mousemove", this.onDrag);
			document.removeEventListener("mouseup", this.stopDrag);
		},

		async loadComments() {
			this.loading = true;
			try {
				const res = await axios.get(
					`/quality-control/comments-by-sde/${this.sdeUuid}`,
				);
				this.comments = res.data.comments;
				this.extractionLocked = !!res.data.extractionLocked;
			} catch (err) {
				console.error("Failed to load SDE comments:", err);
			} finally {
				this.loading = false;
				this.$nextTick(() => {
					this.scrollToHighlighted();
					this.observeUnreadComments();
					this.landOnHighlightedComment();
				});
			}
		},

		scrollToHighlighted() {
			if (!this.highlightCommentUuid) return;
			const container = this.$refs.commentList;
			if (!container) return;
			const el = [...container.querySelectorAll("[data-comment-uuid]")].find(
				(e) => e.dataset.commentUuid === this.highlightCommentUuid,
			);
			if (el) {
				container.scrollTop = el.offsetTop - container.offsetTop - 4;
			}
		},

		isHighlightedComment(comment) {
			if (!this.highlightCommentUuid) return false;
			if (comment.uuid === this.highlightCommentUuid) return true;
			return comment.replies?.some((r) => r.uuid === this.highlightCommentUuid);
		},

		async submitReply(comment) {
			const text = this.replyTexts[comment.uuid]?.trim();
			if (!text || !comment.qcUuid) return;

			try {
				await axios.post(`/quality-control/${comment.qcUuid}/comments`, {
					step: comment.step,
					text,
					parentId: comment.uuid,
				});
				this.replyTexts[comment.uuid] = "";
				await this.loadComments();
			} catch (err) {
				console.error("Failed to submit reply:", err);
			}
		},

		observeUnreadComments() {
			this.teardownReadObserver();

			const container = this.$refs.commentList;
			if (!container) return;

			this._readTimers = {};
			this._pendingReads = {};
			this._readFlushTimer = null;

			this._readObserver = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						const uuid = entry.target.dataset.commentUuid;
						if (!uuid) continue;
						if (entry.isIntersecting) {
							if (!this._readTimers[uuid]) {
								this._readTimers[uuid] = setTimeout(() => {
									const comment = this.findComment(uuid);
									const qcUuid = comment?.qcUuid;
									if (qcUuid) {
										if (!this._pendingReads[qcUuid]) {
											this._pendingReads[qcUuid] = [];
										}
										this._pendingReads[qcUuid].push(uuid);
										this.scheduleReadFlush();
									}
									this.markLocalRead(uuid);
									delete this._readTimers[uuid];
								}, 1500);
							}
						} else {
							if (this._readTimers[uuid]) {
								clearTimeout(this._readTimers[uuid]);
								delete this._readTimers[uuid];
							}
						}
					}
				},
				{ root: container, threshold: 0.5 },
			);

			const els = container.querySelectorAll("[data-comment-uuid]");
			for (const el of els) {
				const uuid = el.dataset.commentUuid;
				const comment = this.findComment(uuid);
				if (comment && comment.read === false) {
					this._readObserver.observe(el);
				}
			}
		},

		scheduleReadFlush() {
			if (this._readFlushTimer) return;
			this._readFlushTimer = setTimeout(() => {
				this._readFlushTimer = null;
				for (const [qcUuid, uuids] of Object.entries(this._pendingReads)) {
					if (uuids.length === 0) continue;
					axios
						.post(`/quality-control/${qcUuid}/comments/mark-read`, {
							commentUuids: uuids,
						})
						.catch((err) => console.warn("Mark-read failed:", err.message));
				}
				this._pendingReads = {};
			}, 2000);
		},

		markLocalRead(uuid) {
			for (const c of this.comments) {
				if (c.uuid === uuid) {
					c.read = true;
					return;
				}
				for (const r of c.replies || []) {
					if (r.uuid === uuid) {
						r.read = true;
						return;
					}
				}
			}
		},

		findComment(uuid) {
			for (const c of this.comments) {
				if (c.uuid === uuid) return c;
				for (const r of c.replies || []) {
					if (r.uuid === uuid) return r;
				}
			}
			return null;
		},

		teardownReadObserver() {
			if (this._readObserver) {
				this._readObserver.disconnect();
				this._readObserver = null;
			}
			if (this._readTimers) {
				for (const t of Object.values(this._readTimers)) {
					clearTimeout(t);
				}
				this._readTimers = null;
			}
			if (this._readFlushTimer) {
				clearTimeout(this._readFlushTimer);
				this._readFlushTimer = null;
			}
		},

		async markUnread(comment) {
			if (!comment.qcUuid) return;
			try {
				await axios.put(
					`/quality-control/${comment.qcUuid}/comments/${comment.uuid}/unread`,
				);
				comment.read = false;
			} catch (err) {
				console.error("Failed to mark comment as unread:", err);
			}
		},

		isOwnReply(reply) {
			return reply.user?.id === this.currentUserId || useMainStore().isAdmin;
		},

		deleteReply(reply, parentComment) {
			this.deleteTarget = reply;
			this.deleteParent = parentComment;
			this.deleteDialog = true;
		},

		async confirmDelete() {
			if (!this.deleteTarget || !this.deleteParent?.qcUuid) return;
			this.deleteDialog = false;
			try {
				await axios.delete(
					`/quality-control/${this.deleteParent.qcUuid}/comments/${this.deleteTarget.uuid}`,
				);
				await this.loadComments();
			} catch (err) {
				console.error("Failed to delete reply:", err);
			}
			this.deleteTarget = null;
			this.deleteParent = null;
		},

		isCommentForActiveDataPoint(comment) {
			if (!this.activeDataPointUuid) return false;
			const uuids = comment.context?.dataPointUuids || [];
			return uuids.includes(this.activeDataPointUuid);
		},

		ownPoints(comment) {
			return ownDataPointUuids(
				comment.context,
				this.sdeUuid,
				this.ownsDataPoint,
			);
		},

		navigateTo(comment) {
			const candidates = this.ownPoints(comment);
			if (candidates.length > 0) {
				this.$emit("navigate", candidates[0], candidates);
			}
		},

		navigateFigure(view) {
			if (view?.graphUuid) {
				this.$emit("navigate-figure", {
					graphUuid: view.graphUuid,
					compositeKey: view.compositeKey || null,
				});
			}
		},

		// What a click on the comment's link does: a figure comment opens its
		// figure (or series), any other its first own point.
		openComment(comment) {
			const view = this.figureViews[comment.uuid];
			if (view) {
				this.navigateFigure(view);
			} else {
				this.navigateTo(comment);
			}
		},

		// A notification link names its comment; open where it points, once.
		landOnHighlightedComment() {
			const uuid = this.highlightCommentUuid;
			if (!this.landOnHighlighted || !uuid) return;
			if (this.landedCommentUuid === uuid) return;
			const comment = commentForUuid(this.comments, uuid);
			if (!comment) return;
			this.landedCommentUuid = uuid;
			this.openComment(comment);
		},

		getUserName(user) {
			return personName(user) || "Unknown";
		},

		formatTime(dateStr) {
			if (!dateStr) return "";
			const date = new Date(dateStr);
			const now = new Date();
			const diffMs = now - date;
			const diffMin = Math.floor(diffMs / 60000);
			if (diffMin < 1) return "now";
			if (diffMin < 60) return `${diffMin}m ago`;
			const diffHr = Math.floor(diffMin / 60);
			if (diffHr < 24) return `${diffHr}h ago`;
			const diffDays = Math.floor(diffHr / 24);
			if (diffDays < 7) return `${diffDays}d ago`;
			return date.toLocaleDateString();
		},
	},
};
</script>

<style scoped>
.qc-comments-panel {
	position: fixed;
	z-index: 2500;
	width: 380px;
	max-height: 500px;
	background: white;
	border-radius: 8px;
	overflow: hidden;
}

.panel-header {
	user-select: none;
}

.drag-handle {
	cursor: grab;
}

.drag-handle:active {
	cursor: grabbing;
}

.comment-item {
	background: rgba(0, 0, 0, 0.03);
	border: 1px solid var(--border-default);
}

/* DCTL-2294: was #d32f2f. Reads the theme key so the rust value stays owned by
   plugins/vuetify.js and a re-bucketing is a token-file change. */
.rework-item {
	border-left: 3px solid rgb(var(--v-theme-state-rework));
}

.active-dp-item {
	outline: 2px solid #347b99;
	outline-offset: -1px;
}

.highlighted-comment {
	outline: 2px solid #347b99;
	outline-offset: -1px;
}

.delete-btn {
	color: rgba(0, 0, 0, 0.4) !important;
}

.delete-btn:hover {
	color: #d32f2f !important;
}

.mark-unread-btn:hover {
	color: #39c9bb !important;
}

.reply-action-slot {
	width: 28px;
	height: 28px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.reply-action-slot .mark-unread-btn {
	margin: 0;
}

.unread-dot-inline {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background: #39c9bb;
	box-shadow: 0 0 6px 2px rgba(57, 201, 187, 0.4);
}

.comment-unread-btn {
	position: absolute;
	top: 2px;
	right: 2px;
}

.unread-dot {
	position: absolute;
	top: 4px;
	right: 4px;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background: #39c9bb;
	box-shadow: 0 0 6px 2px rgba(57, 201, 187, 0.4);
}

.navigate-link:hover {
	text-decoration: underline;
}

/* A figure comment's selected points, one link each; a long selection
   scrolls rather than pushing the comment text out of the card. */
.figure-point-list {
	list-style: none;
	padding-left: 16px;
	max-height: 120px;
	overflow-y: auto;
}

.cursor-pointer {
	cursor: pointer;
}

.border-b {
	border-bottom: 1px solid var(--border-default);
}

.border-s {
	border-left: 1px solid var(--border-default);
}
</style>
